import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircleQuestion, Send, Trash2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAiChatStore } from '@flows.cash/core';

const SUGGESTED_QUESTIONS = [
  'How much did I spend on groceries last month?',
  'Am I on track with my budgets this month?',
  'What subscriptions am I paying for?',
  'What were my biggest expenses this month?',
];

// "Ask your money" — chat over the user's own financial data. Answers are
// grounded in SQL via the money-chat Edge Function's tool loop; history is
// kept in memory only and can be cleared any time.
export default function MoneyChat() {
  const { t, i18n } = useTranslation('common');
  const { messages, isSending, error, send, clear } = useAiChatStore();
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const locale = (i18n.language || 'en').slice(0, 2);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  const handleSend = (text?: string) => {
    const value = (text ?? input).trim();
    if (!value || isSending) return;
    setInput('');
    send(value, locale);
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-background-primary dark:bg-background-dark-primary">
      <div className="flex-1 flex flex-col max-w-3xl w-full mx-auto p-4 sm:p-6 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircleQuestion className="h-6 w-6 text-secondary dark:text-primary-dark" />
            <h1 className="text-xl font-bold text-text-primary dark:text-text-dark-primary">
              {t('moneyChat.title', 'Ask your money')}
            </h1>
          </div>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clear} disabled={isSending}>
              <Trash2 className="h-4 w-4 mr-1.5" />
              {t('moneyChat.clear', 'Clear')}
            </Button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 pb-4 min-h-0">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <Sparkles className="h-8 w-8 text-secondary dark:text-primary-dark mb-3" />
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-6 max-w-md">
                {t(
                  'moneyChat.empty',
                  'Ask anything about your spending, budgets, accounts, or subscriptions. Answers come straight from your data.'
                )}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => handleSend(q)}
                    className="text-xs px-3 py-2 rounded-full border border-border dark:border-border-dark text-text-secondary dark:text-text-dark-secondary hover:border-secondary dark:hover:border-primary-dark hover:text-secondary dark:hover:text-primary-dark transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-primary dark:bg-primary-dark text-white rounded-br-md'
                    : 'bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary rounded-bl-md'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}

          {isSending && (
            <div className="flex justify-start">
              <div className="px-4 py-2.5 rounded-2xl rounded-bl-md bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark">
                <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('moneyChat.thinking', 'Checking your data…')}
                </div>
              </div>
            </div>
          )}

          {error === 'rate_limited' && (
            <p className="text-xs text-center text-amber-600 dark:text-amber-400">
              {t('moneyChat.rateLimited', 'Too many messages — take a short break and try again.')}
            </p>
          )}
          {error === 'chat_unavailable' && (
            <p className="text-xs text-center text-text-secondary dark:text-text-dark-secondary">
              {t('moneyChat.unavailable', "Couldn't get an answer right now — try again.")}
            </p>
          )}
          {error === 'message_too_long' && (
            <p className="text-xs text-center text-text-secondary dark:text-text-dark-secondary">
              {t('moneyChat.tooLong', 'That message is too long — keep it under 2000 characters.')}
            </p>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="shrink-0 pt-2">
          <div className="flex items-end gap-2">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t('moneyChat.placeholder', 'e.g. how much did I spend on eating out in June?')}
              maxLength={2000}
              disabled={isSending}
              className="flex-1 resize-none px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors"
            />
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || isSending}
              className="rounded-xl h-11 px-4"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-text-secondary/70 dark:text-text-dark-secondary/70 text-center">
            {t(
              'moneyChat.disclaimer',
              'Answers are generated by AI from your recorded data — informational, not financial advice.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
